import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useScript } from '@hooks/useScript';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.min.js';
import 'leaflet/dist/leaflet.css';
import {
    CreateAccountPage,
    LoginPage,
    PasswordRecoveryPage,
    SetNewPasswordPage,
    VerifyAccountPage,
} from '@pages/authentication';
import reportWebVitals from './reportWebVitals';
import './i18n/config';
import '@styles/GlobalStyles.css';
import { SiteSetup } from '@pages/SiteSetup';
import { PageNotFound } from '@pages/pageNotFound';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { useGetAllLanguages } from '@services/useGetAllLanguages';
import { DataProvider, NotificationsProvider, ThemesProvider } from '@context';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { SpacesListPage } from '@pages/spaces';
import { SpaceDetailPage } from '@pages/spaceDetailPage/SpaceDetailPage';
import { HomePage } from '@pages/home';
import { ContactsPage } from '@pages/contacts';
import { SpacesManagement, AlertsPage, CreateSpace, EditSpace } from '@pages/admin';
import { MyProfilePage } from '@pages/myProfile';

export default function App() {
    useScript({ url: 'https://kit.fontawesome.com/18bb4f4911.js' });

    const { allLanguages, allSettings } = useData();
    const { request: requestAllLanguages, loading: loadingAllLanguages } = useGetAllLanguages();

    const location = useLocation();
    const currentPath = location.pathname;
    const urlSegments = currentPath.split('/');
    const [currentFaviconSrc, setCurrentFaviconSrc] = useState('');

    // Fetch languages
    useEffect(() => {
        !allLanguages && !loadingAllLanguages && requestAllLanguages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allLanguages]);

    useEffect(() => {
        if (allSettings?.data) {
            const data = allSettings.data;
            const faviconItem = data.layouts?.find((item) => item.name === 'favicon');

            let finalSrc = null;
            if (faviconItem?.value) {
                finalSrc = faviconItem.value;
            }

            if (finalSrc) {
                setCurrentFaviconSrc(finalSrc);

                const existingLinks = document.querySelectorAll('link[rel*=\'icon\']');
                existingLinks.forEach((link) => link.remove());

                const link = document.createElement('link');
                link.rel = 'icon';
                link.href = finalSrc;
                link.type = 'image/x-icon';

                document.head.appendChild(link);
            }
        }

        return () => {
            if (currentFaviconSrc) {
                const dynamicLink = document.querySelector(`link[href="${currentFaviconSrc}"]`);
                if (dynamicLink) {
                    dynamicLink.remove();
                }
            }
        };
    }, [allSettings, currentFaviconSrc]);

    const supportedLanguages = allLanguages?.data
        ?.filter((item) => item.language_code !== undefined)
        .map((item) => item.language_code);

    // waiting for languages service return
    if (supportedLanguages?.length === 0) {
        return <></>;
    }

    const availableRoutes = () => (
        <>
            {/* redirect root to login */}
            <Route index element={<Navigate to={SITE_URLS.login} replace />} />

            {/* home route */}
            <Route path={SITE_URLS.home} element={<HomePage />} />

            {/* authentication routes */}
            <Route path={SITE_URLS.createAccount} element={<CreateAccountPage />} />
            <Route path={SITE_URLS.login} element={<LoginPage />} />
            <Route path={SITE_URLS.recoverPassword} element={<PasswordRecoveryPage />} />
            <Route path={SITE_URLS.setNewPassword} element={<SetNewPasswordPage />} />
            <Route path={SITE_URLS.verifyAccount} element={<VerifyAccountPage />} />

            {/* spaces pages (public) */}
            <Route path={SITE_URLS.spaces} element={<SpacesListPage />} />
            <Route path={SITE_URLS.spaceDetail} element={<SpaceDetailPage />} />

            {/* my account routes */}
            <Route path={SITE_URLS.myProfile} element={<MyProfilePage />} />
            <Route path={SITE_URLS.mySpaces} element={<SpacesManagement />} />
            <Route path={SITE_URLS.createSpace} element={<CreateSpace />} />
            <Route path={SITE_URLS.editSpace} element={<EditSpace />} />
            <Route path={SITE_URLS.alerts} element={<AlertsPage />} />


            <Route path={SITE_URLS.contacts} element={<ContactsPage />} />

            <Route path="*" element={<PageNotFound />} />
            {/* marketplace routes */}
        </>
    );

    return (
        <>
            <Routes>
                {urlSegments.length > 1 && supportedLanguages?.includes(urlSegments[1]) ? (
                    <Route path="/:language" element={<SiteSetup />}>
                        {availableRoutes()}
                    </Route>
                ) : (
                    <Route path="/" element={<SiteSetup />}>
                        {availableRoutes()}
                    </Route>
                )}
            </Routes>
        </>
    );
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
    <BrowserRouter>
        <React.StrictMode>
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''}>
                <ThemesProvider>
                    <DataProvider>
                        <NotificationsProvider>
                            <App />
                        </NotificationsProvider>
                    </DataProvider>
                </ThemesProvider>
            </GoogleOAuthProvider>
        </React.StrictMode>
    </BrowserRouter>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
